import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ContactFormData } from '../contact.types';
import './ContactForm.styles.css';

export const ContactForm: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    subject: '',
    message: '',
    consent: false,
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [statusMessage, setStatusMessage] = useState<{ text: string; isError: boolean } | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const { name, value, type } = e.target;
    const resolvedValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: resolvedValue,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!formData.consent) {
      setStatusMessage({
        text: 'Please accept the privacy policy and consent terms to proceed.',
        isError: true,
      });
      return;
    }

    setIsLoading(true);
    setStatusMessage(null);

    try {
      const response = await fetch('/api/send-telegram', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const result = await response.json();
      if (result.success) {
        setFormData({ name: '', email: '', subject: '', message: '', consent: false });
        navigate('/thank-you');
      } else {
        setStatusMessage({
          text: `Failed: ${result.error || 'Please try again.'}`,
          isError: true,
        });
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Network error';
      setStatusMessage({ text: `Error: ${msg}`, isError: true });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="contact-form-container">
      <form className="contact-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="form-label" htmlFor="name">
            Full Name
          </label>
          <input
            type="text"
            id="name"
            name="name"
            className="form-input"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder="Your name"
          />
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="email">
            Email Address
          </label>
          <input
            type="email"
            id="email"
            name="email"
            className="form-input"
            value={formData.email}
            onChange={handleChange}
            required
            placeholder="you@domain.com"
          />
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="subject">
            Subject
          </label>
          <input
            type="text"
            id="subject"
            name="subject"
            className="form-input"
            value={formData.subject}
            onChange={handleChange}
            placeholder="Project inquiry"
          />
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="message">
            Message
          </label>
          <textarea
            id="message"
            name="message"
            className="form-textarea"
            value={formData.message}
            onChange={handleChange}
            required
            rows={4}
            placeholder="Tell me about your project..."
          />
        </div>

        {/* * Statutory DPDP Act 2023 Affirmative Consent Checkbox */}
        <div className="form-group form-consent-group">
          <label className="consent-checkbox-label" htmlFor="consent">
            <input
              type="checkbox"
              id="consent"
              name="consent"
              className="form-checkbox"
              checked={formData.consent}
              onChange={handleChange}
              required
            />
            <span className="consent-text">
              I consent to the collection and processing of my contact details by <strong>Yash Haldiya</strong> in accordance with the{' '}
              <Link to="/privacy" className="legal-inline-link">Privacy Policy</Link> and{' '}
              <Link to="/terms" className="legal-inline-link">Terms & Conditions</Link> for the purpose of responding to my communication.
            </span>
          </label>
        </div>

        {statusMessage && (
          <div className={`form-status-alert ${statusMessage.isError ? 'error' : 'success'}`}>
            {statusMessage.text}
          </div>
        )}

        <button type="submit" className="form-submit-btn" disabled={isLoading}>
          <span>{isLoading ? 'Sending...' : 'Send Message'}</span>
          <i className="fas fa-paper-plane" />
        </button>
      </form>
    </div>
  );
};

export default ContactForm;

